/**
 * MupConsegnaModal — Modal pre-submit per evidenza consegna documenti
 *                    precontrattuali IVASS (Reg. 40/2018 post Provv. 147/2024).
 *
 * Mostra link a MUP + DIP applicabile + 3 checkbox obbligatorie.
 * Il bottone "Conferma e invia" si abilita solo con tutte e 3 spuntate.
 * Al conferma chiama /api/compliance/registra-batch e poi `onConfirm()`.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   onConfirm: () => void        // chiamato dopo registrazione consegna
 *   prodotto: string             // "auto" | "casa" | "vita_rischio" | "rc_pro" | ...
 *   richiestaId?: string         // id lead/richiesta (se già creata)
 *   customerId?: string          // id customer (se loggato)
 */
import { useMemo, useState } from "react";
import { ExternalLink, Shield, X, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Mappa prodotto -> documenti applicabili
const DOCS_PER_PRODOTTO = {
  auto:         { dip_slug: "dip-danni-auto",     dip_tipo: "dip_danni_auto"     },
  casa:         { dip_slug: "dip-danni-casa",     dip_tipo: "dip_danni_casa"     },
  viaggio:      { dip_slug: "dip-danni-viaggio",  dip_tipo: "dip_danni_viaggio"  },
  rc_pro:       { dip_slug: "dip-danni-rc-pro",   dip_tipo: "dip_danni_rc_pro"   },
  professionisanitarie: { dip_slug: "dip-danni-rc-pro", dip_tipo: "dip_danni_rc_pro" },
  vita:         { dip_slug: "dip-vita-rischio",   dip_tipo: "dip_vita_rischio"   },
  vita_rischio: { dip_slug: "dip-vita-rischio",   dip_tipo: "dip_vita_rischio"   },
  aziende:      { dip_slug: "dip-danni-rc-pro",   dip_tipo: "dip_danni_rc_pro"   },
};

export default function MupConsegnaModal({
  open, onClose, onConfirm,
  prodotto = "auto",
  richiestaId = null, customerId = null,
}) {
  const [chk1, setChk1] = useState(false); // MUP
  const [chk2, setChk2] = useState(false); // DIP specifico
  const [chk3, setChk3] = useState(false); // conferma ricezione
  const [chk4, setChk4] = useState(false); // dichiarazione IDD veritiere (solo vita)
  const [submitting, setSubmitting] = useState(false);

  const mapping = DOCS_PER_PRODOTTO[prodotto] || DOCS_PER_PRODOTTO.auto;
  const dipLabel = useMemo(() => mapping.dip_slug.replace(/-/g, " ").toUpperCase(), [mapping]);
  const isVita = prodotto === "vita" || prodotto === "vita_rischio";
  const allChecked = chk1 && chk2 && chk3 && (!isVita || chk4);

  if (!open) return null;

  const handleConfirm = async () => {
    if (!allChecked || submitting) return;
    setSubmitting(true);
    try {
      // Registra batch consegna (MUP + DIP applicabile + dichiarazione IDD se vita)
      const documenti = ["mup", mapping.dip_tipo];
      if (isVita) documenti.push("dichiarazione_idd_veritiere");
      await fetch(`${API}/api/compliance/registra-batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          customer_id: customerId,
          richiesta_id: richiestaId,
          documenti,
          metodo: "web_modal",
          contesto: `wizard_${prodotto}_pre_submit`,
        }),
      });
    } catch (_) { /* non bloccare submit per failure registro */ }
    setSubmitting(false);
    onConfirm?.();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="mup-consegna-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mup-modal-title"
    >
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-zinc-100 px-6 py-4 flex items-center justify-between rounded-t-3xl">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
              <Shield className="w-4 h-4 text-emerald-700" />
            </div>
            <div>
              <h2 id="mup-modal-title" className="font-heading text-base font-semibold text-zinc-900">
                Informativa precontrattuale IVASS
              </h2>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">Reg. 40/2018 — Provv. 147/2024</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-zinc-100" aria-label="Chiudi modale" data-testid="mup-modal-close">
            <X className="w-4 h-4 text-zinc-500" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <p className="text-sm text-zinc-700 leading-relaxed">
            Prima di inviare la richiesta, ti invitiamo a prendere visione della documentazione precontrattuale obbligatoria. La presa visione è una tutela prevista da IVASS a tuo favore.
          </p>

          {/* Documenti link */}
          <div className="space-y-2">
            <a
              href="/legal/mup"
              target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-between gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors"
              data-testid="mup-link-mup"
            >
              <div>
                <p className="text-sm font-semibold text-emerald-900">MUP — Modulo Unico Precontrattuale</p>
                <p className="text-xs text-emerald-700/80">Informazioni sul distributore e modalità di consulenza</p>
              </div>
              <ExternalLink className="w-4 h-4 text-emerald-700" />
            </a>
            <a
              href={`/legal/${mapping.dip_slug}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-between gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors"
              data-testid="mup-link-dip"
            >
              <div>
                <p className="text-sm font-semibold text-emerald-900">{dipLabel}</p>
                <p className="text-xs text-emerald-700/80">Informazioni specifiche del prodotto assicurativo</p>
              </div>
              <ExternalLink className="w-4 h-4 text-emerald-700" />
            </a>
          </div>

          {/* Checkbox */}
          <div className="space-y-3 pt-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={chk1} onChange={(e) => setChk1(e.target.checked)} className="mt-0.5 w-4 h-4 rounded text-emerald-600" data-testid="mup-chk-1" />
              <span className="text-sm text-zinc-700">Ho preso visione del <strong>MUP — Modulo Unico Precontrattuale</strong></span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={chk2} onChange={(e) => setChk2(e.target.checked)} className="mt-0.5 w-4 h-4 rounded text-emerald-600" data-testid="mup-chk-2" />
              <span className="text-sm text-zinc-700">Ho preso visione del <strong>{dipLabel}</strong></span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={chk3} onChange={(e) => setChk3(e.target.checked)} className="mt-0.5 w-4 h-4 rounded text-emerald-600" data-testid="mup-chk-3" />
              <span className="text-sm text-zinc-700">Confermo di aver ricevuto la documentazione precontrattuale</span>
            </label>
            {isVita && (
              <label className="flex items-start gap-3 cursor-pointer p-2.5 rounded-lg bg-amber-50 border border-amber-200" data-testid="mup-chk-4-wrapper">
                <input type="checkbox" checked={chk4} onChange={(e) => setChk4(e.target.checked)} className="mt-0.5 w-4 h-4 rounded text-amber-600" data-testid="mup-chk-4" />
                <span className="text-sm text-zinc-900">
                  Dichiaro che le risposte alla profilatura adeguatezza (IDD) sono <strong>veritiere e complete</strong>
                  <span className="block text-[10px] text-zinc-500 mt-0.5">art. 30 par. 1 IDD Reg. UE 2016/97</span>
                </span>
              </label>
            )}
          </div>

          {/* Actions */}
          <div className="pt-3 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
            <button onClick={onClose} className="px-5 h-11 rounded-full border border-zinc-200 text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors" data-testid="mup-modal-cancel">
              Annulla
            </button>
            <button
              onClick={handleConfirm}
              disabled={!allChecked || submitting}
              className={`inline-flex items-center justify-center gap-2 px-6 h-11 rounded-full text-sm font-semibold transition-all ${
                allChecked
                  ? "bg-[#163321] text-white hover:bg-[#1d4a2e] shadow-lg shadow-[#163321]/20"
                  : "bg-zinc-100 text-zinc-400 cursor-not-allowed"
              }`}
              data-testid="mup-modal-confirm"
            >
              {submitting ? "Invio in corso..." : (<><CheckCircle2 className="w-4 h-4" /> Conferma e invia</>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
